import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useQuery } from 'react-query';
import debounce from 'lodash/debounce';
import {
  CheckOutlined,
  CloseCircleOutlined,
  CreditCardOutlined,
  DisconnectOutlined,
  LinkOutlined,
  PercentageOutlined,
  QuestionCircleOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Row,
  Col,
  Typography,
  Divider,
  Button,
  InputNumber,
  Tooltip,
  Select,
  Space,
  Flex,
  Switch,
  Tag,
  Spin,
} from 'antd';
import styled from 'styled-components';
import { COMPANY_NAME } from '../../../constants';
import { t } from '../../../utils/i18n';
import { useGlobals } from '../../../globals-context';
import useMerchantSettings from '../../../hooks/use-merchant-settings';
import {
  useDisablePayPalIntegration,
  useInitiatePayPalSetup,
  useInitiateShorePayOnlineAccountSetup,
  useFetchSubPaymentMethodsV2,
  // useToggleSubPaymentMethodV2, // TODO: restore when Klarna toggle is ready
  useUpdatePaymentMethodsV2,
  paymentErrors,
} from '../../../shared/hooks/payments';
import { PaymentCaptureTaxIDModal } from './PaymentCaptureTaxIDModal';
import {
  PaymentAvailabilityType,
  PaymentMethodStatus,
  IPaymentMethodV2,
  IPaymentMethodMetaV2,
  IMerchantPaymentSettingsV2,
  ISubPaymentMethodV2,
  SubPaymentMethodNameV2,
} from '../../../shared/types';
import createQueryConfig from '../../../queries';
import {
  VisaIcon,
  MastercardIcon,
  MaestroIcon,
  AmexIcon,
  CbIcon,
  ApplePayIcon,
  GooglePayIcon,
  KlarnaIcon,
} from './PaymentBrandIcons';

const DOWNPAYMENT_MIN = 10;
const NOW_IN_SECONDS = Math.floor(Date.now() / 1000);
const TWO_DAYS_IN_SECONDS = 2 * 24 * 60 * 60;

interface SectionPaymentPolicyProps {
  currentPaymentAvailability: PaymentAvailabilityType;
  onPaymentPolicyChange: (value: PaymentAvailabilityType) => void;
  isLoading: boolean;
  hideOptionalPolicy: boolean;
}

interface SectionPrepaymentAmountProps {
  amount: number;
  onAmountChange: (value: number) => void;
  disabled: boolean;
}

interface ShorePayOnlineCardProps {
  merchantId: string;
  status: PaymentMethodStatus;
  company: string;
}

interface PayPalCardProps {
  merchantId: string;
  status: PaymentMethodStatus;
}

interface SectionPaymentGatewayProps {
  merchantId: string;
  company: string;
  isPayPalFeatureToggleEnabled: boolean;
  paymentMethods: IPaymentMethodV2[];
}

const StyledPaymentSetupPageWrapper = styled.div`
  width: 100%;
  max-width: 650px;
  padding: 0 24px 24px;
`;

const StyledGatewayCard = styled.div`
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  overflow: hidden;
`;

const StyledGatewayCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
`;

const StyledSubPaymentMethodRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
`;

const PAYMENT_POLICY_OPTIONS = [
  {
    value: 'disabled' as PaymentAvailabilityType,
    label: t(
      'application-settings.payments.setup-page.section-booking-payment.payment-types.pay-at-location'
    ),
    icon: <CloseCircleOutlined />,
    description: t(
      'application-settings.payments.setup-page.section-payment-policy.helper.disabled'
    ),
  },
  {
    value: 'mandatory' as PaymentAvailabilityType,
    label: t(
      'application-settings.payments.setup-page.section-booking-payment.payment-types.mandatory'
    ),
    icon: <CreditCardOutlined />,
    description: t(
      'application-settings.payments.setup-page.section-payment-policy.helper.mandatory'
    ),
  },
  {
    value: 'optional' as PaymentAvailabilityType,
    label: t(
      'application-settings.payments.setup-page.section-booking-payment.payment-types.optional'
    ),
    icon: <UserOutlined />,
    description: t(
      'application-settings.payments.setup-page.section-payment-policy.helper.optional'
    ),
  },
];

// ── Section: Payment Policy ──────────────────────────────────────────

function SectionPaymentPolicy({
  currentPaymentAvailability,
  onPaymentPolicyChange,
  isLoading,
  hideOptionalPolicy,
}: SectionPaymentPolicyProps) {
  const options = hideOptionalPolicy
    ? PAYMENT_POLICY_OPTIONS.filter((o) => o.value !== 'optional')
    : PAYMENT_POLICY_OPTIONS;

  const selectedOption = PAYMENT_POLICY_OPTIONS.find(
    (o) => o.value === currentPaymentAvailability
  )!;

  return (
    <Row gutter={[16, 16]}>
      <Col span={24}>
        <Typography.Title level={3} style={{ marginTop: 16, marginBottom: 4 }}>
          {t(
            'application-settings.payments.setup-page.section-payment-policy.title'
          )}
        </Typography.Title>
        <Typography.Text
          type="secondary"
          style={{ display: 'block', maxWidth: 500 }}
        >
          {t(
            'application-settings.payments.setup-page.section-payment-policy.description'
          )}
        </Typography.Text>
      </Col>
      <Col span={24} style={{ maxWidth: 500 }}>
        <Select
          value={currentPaymentAvailability}
          onChange={onPaymentPolicyChange}
          loading={isLoading}
          disabled={isLoading}
          style={{ width: '100%' }}
          size="large"
          labelRender={() => (
            <Space>
              {selectedOption.icon}
              {selectedOption.label}
            </Space>
          )}
          optionRender={(option) => {
            const { icon, label, description } = option.data;
            const isSelected = option.value === currentPaymentAvailability;
            return (
              <Flex gap={8} align="center" style={{ fontWeight: 'normal' }}>
                <div>{icon}</div>
                <Flex vertical style={{ flex: 1, minWidth: 0 }}>
                  <Typography.Text>{label}</Typography.Text>
                  <Typography.Text
                    type="secondary"
                    style={{ whiteSpace: 'normal' }}
                  >
                    {description}
                  </Typography.Text>
                </Flex>
                {isSelected && <CheckOutlined />}
              </Flex>
            );
          }}
          options={options}
        />
        <Typography.Text
          type="secondary"
          style={{ display: 'block', marginTop: 8 }}
        >
          {selectedOption.description}
        </Typography.Text>
        {currentPaymentAvailability === 'optional' && (
          <Alert
            showIcon
            message={t(
              'application-settings.payments.setup-page.section-booking-payment.warning.payment-type-optional'
            )}
            type="warning"
            style={{ marginTop: 8 }}
          />
        )}
      </Col>
    </Row>
  );
}

// ── Section: Prepayment Amount ───────────────────────────────────────

function SectionPrepaymentAmount({
  amount,
  onAmountChange,
  disabled,
}: SectionPrepaymentAmountProps) {
  return (
    <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
      <Col span={24} style={{ maxWidth: 500 }}>
        <Flex align="center" gap={4} style={{ marginBottom: 4 }}>
          <Typography.Text>
            {t(
              'application-settings.payments.setup-page.section-payment-policy.prepayment-amount.label'
            )}
          </Typography.Text>
          <Tooltip
            title={t(
              'application-settings.payments.setup-page.section-payment-policy.prepayment-amount.tooltip'
            )}
          >
            <QuestionCircleOutlined style={{ color: '#bdc3c7' }} />
          </Tooltip>
        </Flex>
        <InputNumber
          size="large"
          min={10}
          max={100}
          value={amount}
          onChange={(val) => val !== null && onAmountChange(val)}
          prefix={<PercentageOutlined />}
          style={{ width: '100%' }}
          disabled={disabled}
        />
        <Typography.Text
          type="secondary"
          style={{ fontSize: 12, marginTop: 4, display: 'block' }}
        >
          {t(
            'application-settings.payments.setup-page.section-payment-policy.prepayment-amount.remaining-hint'
          ).replace('{{remaining}}', String(100 - amount))}
        </Typography.Text>
      </Col>
    </Row>
  );
}

// ── Shore Pay Online Card ────────────────────────────────────────────

function ShorePayOnlineCard({
  merchantId,
  status,
  company,
}: ShorePayOnlineCardProps) {
  const [isCaptureTaxIDModalOpen, setIsCaptureTaxIDModalOpen] = useState(false);

  const {
    mutate: mutateInitiateShorePayOnlineAccountSetup,
    isLoading: isLoadingShorePayOnlineSetup,
  } = useInitiateShorePayOnlineAccountSetup();

  const { data: subPaymentMethods, isLoading: isLoadingSubPaymentMethods } =
    useFetchSubPaymentMethodsV2(merchantId, status !== 'not_setup');

  // TODO: restore when Klarna toggle is ready
  // const { mutate: mutateToggleSubPaymentMethod, isLoading: isTogglingKlarna } =
  //   useToggleSubPaymentMethodV2();

  // const handleToggle = (
  //   name: SubPaymentMethodNameV2,
  //   currentEnabled: boolean
  // ) => {
  //   mutateToggleSubPaymentMethod({
  //     merchantId,
  //     subPaymentMethodName: name,
  //     enabled: !currentEnabled,
  //   });
  // };

  const handleSetup = () => {
    mutateInitiateShorePayOnlineAccountSetup(
      { company, merchantId },
      {
        onError: (error: unknown) => {
          if (
            error instanceof Error &&
            error.message.includes(paymentErrors.vatIdOrTaxNumberRequired)
          ) {
            setIsCaptureTaxIDModalOpen(true);
          }
        },
      }
    );
  };

  const handleCaptureTaxIDSubmit = ({ taxId }: { taxId: string }) => {
    mutateInitiateShorePayOnlineAccountSetup(
      { taxId, company, merchantId },
      { onSuccess: () => setIsCaptureTaxIDModalOpen(false) }
    );
  };

  if (status === 'not_setup') {
    return (
      <StyledGatewayCard>
        <StyledGatewayCardHeader>
          <Typography.Text strong style={{ fontSize: 14 }}>
            Shore Pay Online
          </Typography.Text>
          <Button
            icon={<LinkOutlined />}
            type="primary"
            loading={isLoadingShorePayOnlineSetup}
            onClick={handleSetup}
          >
            {t(
              'application-settings.payments.setup-page.section-payment-gateways.status.setup'
            )}
          </Button>
        </StyledGatewayCardHeader>
        {isCaptureTaxIDModalOpen && (
          <PaymentCaptureTaxIDModal
            isLoadingShorePayOnlineSetup={isLoadingShorePayOnlineSetup}
            onClickClose={() => setIsCaptureTaxIDModalOpen(false)}
            onFormSubmit={handleCaptureTaxIDSubmit}
          />
        )}
      </StyledGatewayCard>
    );
  }

  type SubPaymentMethodsMap = Record<
    SubPaymentMethodNameV2,
    ISubPaymentMethodV2
  >;
  const methods = Object.fromEntries(
    subPaymentMethods.map((m) => [m.name, m])
  ) as SubPaymentMethodsMap;

  return (
    <StyledGatewayCard>
      <StyledGatewayCardHeader>
        <Flex align="center" gap={8}>
          <Typography.Text strong style={{ fontSize: 14 }}>
            Shore Pay Online
          </Typography.Text>
          <Tag color="green" bordered>
            {t(
              'application-settings.payments.setup-page.section-payment-gateways.status.active'
            )}
          </Tag>
        </Flex>
      </StyledGatewayCardHeader>
      <Divider style={{ margin: 0 }} />
      {isLoadingSubPaymentMethods ? (
        <Flex justify="center" style={{ padding: 24 }}>
          <Spin />
        </Flex>
      ) : (
        <>
          <StyledSubPaymentMethodRow>
            <Flex align="center" gap={8}>
              <Typography.Text>
                {t(
                  'application-settings.payments.setup-page.section-payment-gateways.credit-card-payment'
                )}
              </Typography.Text>
              <Flex gap={4}>
                <VisaIcon />
                <MastercardIcon />
                <MaestroIcon />
                <AmexIcon />
                <CbIcon />
              </Flex>
            </Flex>
            <Switch
              checked={methods.Card.enabled}
              disabled
              style={{ opacity: 0.4 }}
            />
          </StyledSubPaymentMethodRow>
          <StyledSubPaymentMethodRow>
            <Flex align="center" gap={8}>
              <Typography.Text>Apple Pay</Typography.Text>
              <ApplePayIcon />
            </Flex>
            <Switch
              checked={methods.ApplePay.enabled}
              disabled
              style={{ opacity: 0.4 }}
            />
          </StyledSubPaymentMethodRow>
          <StyledSubPaymentMethodRow>
            <Flex align="center" gap={8}>
              <Typography.Text>Google Pay</Typography.Text>
              <GooglePayIcon />
            </Flex>
            <Switch
              checked={methods.GooglePay.enabled}
              disabled
              style={{ opacity: 0.4 }}
            />
          </StyledSubPaymentMethodRow>
          <Divider style={{ margin: 0 }} />
          <StyledSubPaymentMethodRow>
            <Flex align="center" gap={8}>
              <Typography.Text>Klarna</Typography.Text>
              <KlarnaIcon />
            </Flex>
            <Tag>
              {t(
                'application-settings.payments.setup-page.section-payment-gateways.status.coming-soon'
              )}
            </Tag>
            {/* <Switch
              checked={methods.Klarna.enabled}
              loading={isTogglingKlarna || isFetchingSubPaymentMethods}
              disabled={isTogglingKlarna || isFetchingSubPaymentMethods}
              onChange={() => handleToggle('Klarna', methods.Klarna.enabled)}
            /> */}
          </StyledSubPaymentMethodRow>
        </>
      )}
    </StyledGatewayCard>
  );
}

// ── PayPal Card ──────────────────────────────────────────────────────

function PayPalCard({ merchantId, status }: PayPalCardProps) {
  const { trackMethod } = useGlobals()!;
  const isActive = status === 'active';

  const {
    mutate: mutateDisablePaypalIntegration,
    isLoading: isLoadingDisablePaypalIntegration,
  } = useDisablePayPalIntegration();

  const {
    mutate: mutateInitiatePayPalSetup,
    isLoading: isLoadingInitiatePayPalSetup,
  } = useInitiatePayPalSetup();

  const handleDisconnect = () => {
    trackMethod(
      'application-settings-app_online_payment_settings_screen_disable_paypal_modal_opened'
    );
    mutateDisablePaypalIntegration({ merchantId });
  };

  const handleConnect = () => {
    mutateInitiatePayPalSetup({ merchantId });
  };

  return (
    <StyledGatewayCard>
      <StyledGatewayCardHeader>
        <Flex align="center" gap={8}>
          <Typography.Text strong style={{ fontSize: 14 }}>
            PayPal
          </Typography.Text>
          {isActive && (
            <Tag color="green" bordered>
              {t(
                'application-settings.payments.setup-page.section-payment-gateways.status.active'
              )}
            </Tag>
          )}
        </Flex>
        {isActive ? (
          <Button
            icon={<DisconnectOutlined />}
            loading={isLoadingDisablePaypalIntegration}
            onClick={handleDisconnect}
          >
            {t(
              'application-settings.payments.setup-page.section-payment-gateways.status.disconnect'
            )}
          </Button>
        ) : (
          <Button
            icon={<LinkOutlined />}
            loading={isLoadingInitiatePayPalSetup}
            onClick={handleConnect}
            type="primary"
          >
            {t(
              'application-settings.payments.setup-page.section-payment-gateways.status.connect'
            )}
          </Button>
        )}
      </StyledGatewayCardHeader>
      <Divider style={{ margin: 0 }} />
      <div style={{ padding: 16 }}>
        <Typography.Text type="secondary">
          {t(
            'application-settings.payments.setup-page.section-payment-gateways.paypal-description'
          )}
        </Typography.Text>
      </div>
    </StyledGatewayCard>
  );
}

// ── Section: Payment Gateways ────────────────────────────────────────

function SectionPaymentGateway({
  merchantId,
  company,
  paymentMethods,
  isPayPalFeatureToggleEnabled,
}: SectionPaymentGatewayProps) {
  const tooltipText = isPayPalFeatureToggleEnabled
    ? t(
        'application-settings.payments.setup-page.section-payment-gateways.title-tooltip'
      )
    : t(
        'application-settings.payments.setup-page.section-payment-gateways.title-tooltip-for-stripe-only'
      );

  const shorePayOnline = paymentMethods.find(
    (m) => m.name === 'shorepay_online'
  );
  const paypal = paymentMethods.find((m) => m.name === 'paypal');

  return (
    <Row gutter={[16, 16]}>
      <Col span={24}>
        <Typography.Title level={3} style={{ marginTop: 0, marginBottom: 4 }}>
          {t(
            'application-settings.payments.setup-page.section-payment-gateways.title'
          )}
          <Tooltip title={tooltipText}>
            <QuestionCircleOutlined
              style={{
                fontSize: 18,
                marginLeft: 8,
                color: '#bdc3c7',
              }}
            />
          </Tooltip>
        </Typography.Title>
        <Typography.Text
          type="secondary"
          style={{ display: 'block', maxWidth: 500 }}
        >
          <span
            dangerouslySetInnerHTML={{
              __html: t(
                'application-settings.payments.setup-page.section-payment-gateways.description'
              ),
            }}
          />
        </Typography.Text>
      </Col>
      <Col span={24}>
        <Flex vertical gap={16} style={{ maxWidth: 500 }}>
          {shorePayOnline && (
            <ShorePayOnlineCard
              merchantId={merchantId}
              status={shorePayOnline.status}
              company={company}
            />
          )}
          {paypal && isPayPalFeatureToggleEnabled && (
            <PayPalCard merchantId={merchantId} status={paypal.status} />
          )}
        </Flex>
      </Col>
    </Row>
  );
}

// ── Main Component ───────────────────────────────────────────────────

export function PaymentSetupPageV2() {
  const { queryClient, apiClient } = useGlobals()!;
  const queryConfig = createQueryConfig(queryClient, apiClient);

  const { merchantSettings } = useMerchantSettings();
  const { merchantId, company, featureToggles } = merchantSettings || {};

  const {
    data: paymentSettingsV2,
    isLoading,
    isFetching: isFetchingPaymentSettings,
  } = useQuery<IMerchantPaymentSettingsV2>({
    ...queryConfig.merchantPaymentSettingsV2b({ merchantId }),
    enabled: Boolean(merchantId),
  });

  const {
    mutate: mutateUpdatePaymentMethods,
    isLoading: isUpdatingPaymentPolicy,
  } = useUpdatePaymentMethodsV2();

  const paymentMethods: IPaymentMethodV2[] = (
    paymentSettingsV2?.payment_methods ?? []
  ).filter(({ name, status }) => {
    if (!(featureToggles?.paypal_payment_enabled ?? false) && name === 'paypal')
      return false;
    if (COMPANY_NAME !== 'shore' && name === 'shorepay_online') return false;
    if (name === 'stripe' && status === 'not_setup') return false;
    return true;
  });

  const bookingConfigs = useSelector((state: any) => state.bookingConfigs);

  const shorepayOnlineActivatedAt: number =
    bookingConfigs?.shorepayOnlineActivatedAt ?? 0;

  const isShorePayOnlineActive =
    paymentMethods.find((m) => m.name === 'shorepay_online')?.status ===
    'active';
  const isWithinFirstTwoDays =
    NOW_IN_SECONDS < shorepayOnlineActivatedAt + TWO_DAYS_IN_SECONDS;
  const hideOptionalPolicy = isShorePayOnlineActive && isWithinFirstTwoDays;

  const shorePayOnlineMeta: IPaymentMethodMetaV2 | undefined =
    paymentMethods.find((m) => m.name === 'shorepay_online')?.meta;

  const [prepaymentAmount, setPrepaymentAmount] = useState<number>(
    shorePayOnlineMeta?.down_payment.percentage ?? 100
  );

  useEffect(() => {
    if (shorePayOnlineMeta?.down_payment.percentage !== undefined) {
      setPrepaymentAmount(shorePayOnlineMeta.down_payment.percentage);
    }
  }, [shorePayOnlineMeta?.down_payment.percentage]);

  const debouncedUpdateDownPayment = useMemo(
    () =>
      debounce((percentage: number) => {
        mutateUpdatePaymentMethods({
          merchantId,
          payload: {
            payment_methods: [
              {
                name: 'shorepay_online',
                meta: { down_payment: { enabled: true, percentage } },
              },
            ],
          },
        });
      }, 500),
    [merchantId, mutateUpdatePaymentMethods]
  );

  const isPayPalFeatureToggleEnabled =
    featureToggles?.paypal_payment_enabled || false;

  const paymentPolicy = paymentSettingsV2?.payment_policy ?? 'disabled';

  const handleOnPaymentPolicyChange = (value: PaymentAvailabilityType) => {
    switch (value) {
      case 'mandatory':
        mutateUpdatePaymentMethods({
          merchantId,
          payload: {
            payment_policy: 'mandatory',
            payment_methods: isShorePayOnlineActive
              ? [
                  {
                    name: 'shorepay_online',
                    meta: {
                      down_payment: {
                        enabled: true,
                        percentage: prepaymentAmount || DOWNPAYMENT_MIN,
                      },
                    },
                  },
                ]
              : [],
          },
        });
        break;
      case 'disabled':
        mutateUpdatePaymentMethods({
          merchantId,
          payload: { payment_policy: 'disabled' },
        });
        break;
      case 'optional':
        mutateUpdatePaymentMethods({
          merchantId,
          payload: { payment_policy: 'optional' },
        });
        break;
      default:
        console.warn('Unknown payment policy:', value);
    }
  };

  const handlePrepaymentAmountChange = (value: number) => {
    setPrepaymentAmount(value);
    debouncedUpdateDownPayment(value);
  };

  if (isLoading) {
    return (
      <Flex justify="center" style={{ padding: 48 }}>
        <Spin />
      </Flex>
    );
  }

  return (
    <StyledPaymentSetupPageWrapper>
      <SectionPaymentPolicy
        currentPaymentAvailability={paymentPolicy}
        onPaymentPolicyChange={handleOnPaymentPolicyChange}
        isLoading={isUpdatingPaymentPolicy || isFetchingPaymentSettings}
        hideOptionalPolicy={hideOptionalPolicy}
      />
      {paymentPolicy !== 'disabled' && (
        <SectionPrepaymentAmount
          // When shorepay_online is not_setup but PayPal is active and policy is mandatory,
          // the input is disabled and the backend won't return 100 — so we fall back to 100 here.
          amount={
            paymentPolicy === 'optional' || !isShorePayOnlineActive
              ? 100
              : prepaymentAmount
          }
          onAmountChange={handlePrepaymentAmountChange}
          disabled={
            !isShorePayOnlineActive ||
            paymentPolicy === 'optional' ||
            isUpdatingPaymentPolicy ||
            isFetchingPaymentSettings
          }
        />
      )}
      <Divider />
      <SectionPaymentGateway
        merchantId={merchantId}
        company={company}
        paymentMethods={paymentMethods}
        isPayPalFeatureToggleEnabled={isPayPalFeatureToggleEnabled}
      />
    </StyledPaymentSetupPageWrapper>
  );
}
